"use client";

import { useState } from "react";
import {
  Drawer,
  Stack,
  ErrorMessage,
  TextOrHTML,
} from "@simplybusiness/mobius";
import type { PaymentTermsChecklistProps, AgreementsContent } from "./types";
import { CheckboxItem } from "./CheckboxItem";

export const PaymentTermsChecklist = ({
  id,
  options,
  value,
  defaultValue = [],
  onChange,
  isDisabled = false,
  isInvalid = false,
  errorMessage,
  drawerCloseLabel = "Close",
  checkboxLinkClickEvent,
}: PaymentTermsChecklistProps) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState<string[]>(defaultValue);
  const currentValue = isControlled ? value : internalValue;
  const [drawerContent, setDrawerContent] = useState<
    AgreementsContent[] | null
  >(null);

  const handleCheckboxChange = (optionValue: string, checked: boolean) => {
    const newValue = checked
      ? [...currentValue, optionValue]
      : currentValue.filter(v => v !== optionValue);

    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const errorId = `${id}-error`;

  return (
    <>
      <div
        id={id}
        role="group"
        aria-describedby={errorMessage ? errorId : undefined}
        className="mobius mobius-payment-terms-checklist"
      >
        <Stack gap="sm">
          {options.map(option => (
            <CheckboxItem
              key={option.value}
              option={option}
              isChecked={currentValue.includes(option.value)}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
              onCheckboxChange={handleCheckboxChange}
              onLinkClick={agreements => {
                setDrawerContent(agreements);
                if (checkboxLinkClickEvent) {
                  checkboxLinkClickEvent(option.value);
                }
              }}
              id={`${id}-${option.value}`}
            />
          ))}
        </Stack>
        {errorMessage && (
          <ErrorMessage errorMessage={errorMessage} id={errorId} />
        )}
      </div>

      <Drawer
        isOpen={drawerContent !== null}
        direction="right"
        closeLabel={drawerCloseLabel}
        onClose={() => setDrawerContent(null)}
      >
        {drawerContent && drawerContent.length > 0 && (
          <>
            <Drawer.Header>{}</Drawer.Header>
            <Drawer.Content>
              <Stack gap="lg">
                {drawerContent.map((agreement, index) => (
                  <div key={index}>
                    <h3>{agreement.label || agreement.title}</h3>
                    <TextOrHTML
                      text={agreement.content}
                      htmlElementType="div"
                    />
                  </div>
                ))}
              </Stack>
            </Drawer.Content>
          </>
        )}
      </Drawer>
    </>
  );
};
